import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { CRM_LABEL_MAX_SELECTION, CrmLabel } from '../../../crm-models';
import { CrmServiceService } from '../../../service/crm-service.service';

@Component({
  selector: 'app-crm-label-picker',
  templateUrl: './crm-label-picker.component.html',
  styleUrls: ['./crm-label-picker.component.scss']
})
export class CrmLabelPickerComponent implements OnInit, OnChanges {
  @Input() labelIds: string[] = [];
  @Input() labels: CrmLabel[] = [];
  @Input() disabled = false;
  @Output() labelIdsChange = new EventEmitter<string[]>();
  @Output() labelsChange = new EventEmitter<CrmLabel[]>();

  readonly maxSelection = CRM_LABEL_MAX_SELECTION;

  activeLabels: CrmLabel[] = [];
  selectedIds: string[] = [];
  selectedLabels: CrmLabel[] = [];
  loading = false;
  searchText = '';
  open = false;

  constructor(private crmService: CrmServiceService) {}

  ngOnInit(): void {
    this.syncFromInputs();
    this.loadActiveLabels();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['labelIds'] || changes['labels']) {
      this.syncFromInputs();
    }
  }

  get filteredActiveLabels(): CrmLabel[] {
    const q = (this.searchText || '').trim().toLowerCase();
    if (!q) {
      return this.activeLabels;
    }
    return this.activeLabels.filter((l) => (l.name || '').toLowerCase().includes(q));
  }

  get atLimit(): boolean {
    return this.selectedIds.length >= this.maxSelection;
  }

  isSelected(id: string): boolean {
    return this.selectedIds.includes(id);
  }

  chipStyle(label: CrmLabel): { [key: string]: string } {
    const color = label?.color || '#6B7280';
    return {
      'background-color': this.hexWithAlpha(color, label?.active === false ? 0.25 : 0.15),
      color,
      'border-color': this.hexWithAlpha(color, label?.active === false ? 0.35 : 0.45)
    };
  }

  private hexWithAlpha(hex: string, alpha: number): string {
    const cleaned = (hex || '').replace('#', '');
    if (cleaned.length !== 6) {
      return `rgba(107, 114, 128, ${alpha})`;
    }
    const r = parseInt(cleaned.slice(0, 2), 16);
    const g = parseInt(cleaned.slice(2, 4), 16);
    const b = parseInt(cleaned.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  toggleDropdown(): void {
    if (this.disabled) {
      return;
    }
    this.open = !this.open;
  }

  toggleLabel(label: CrmLabel): void {
    if (this.disabled || !label?.id || label.active === false) {
      return;
    }
    if (this.isSelected(label.id)) {
      this.removeLabel(label.id);
      return;
    }
    if (this.atLimit) {
      return;
    }
    this.selectedIds = [...this.selectedIds, label.id];
    this.rebuildSelectedLabels();
    this.emitChanges();
  }

  removeLabel(id: string): void {
    if (this.disabled) {
      return;
    }
    this.selectedIds = this.selectedIds.filter((x) => x !== id);
    this.rebuildSelectedLabels();
    this.emitChanges();
  }

  private syncFromInputs(): void {
    this.selectedIds = [...(this.labelIds || [])];
    this.rebuildSelectedLabels();
  }

  private rebuildSelectedLabels(): void {
    const byId = new Map<string, CrmLabel>();
    (this.labels || []).forEach((l) => {
      if (l?.id) {
        byId.set(l.id, l);
      }
    });
    this.activeLabels.forEach((l) => {
      if (l?.id && !byId.has(l.id)) {
        byId.set(l.id, l);
      }
    });
    this.selectedLabels = this.selectedIds
      .map((id) => byId.get(id))
      .filter((l): l is CrmLabel => !!l);
  }

  private emitChanges(): void {
    this.labelIdsChange.emit([...this.selectedIds]);
    this.labelsChange.emit([...this.selectedLabels]);
  }

  private loadActiveLabels(): void {
    const businessId = localStorage.getItem('businessId') || '';
    if (!businessId) {
      return;
    }
    this.loading = true;
    this.crmService.listLabels(businessId, false).subscribe({
      next: (res: any) => {
        const data = res?.data ?? res ?? [];
        this.activeLabels = Array.isArray(data) ? data : [];
        this.rebuildSelectedLabels();
        this.loading = false;
      },
      error: () => {
        this.activeLabels = [];
        this.loading = false;
      }
    });
  }
}
